import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ATSPrincipalAppCandidateModalTabsTestIds } from 'data-testids/ATS';
import { ExperienceItemProps, ExperienceTabProps } from 'features/Principal/types/modalTypes';
import { DateText, GreyText, ItalicsText, NoneFoundText, StackRow, SubHeader } from '../../styles';
import { BasicInfoBox, Subtitle } from '../../styles';

export const ExperienceItem: React.FC<ExperienceItemProps> = ({ position }) => {
  const theme = useTheme();
  const endDate =
    position.end_date === null
      ? 'Present'
      : new Date(position.end_date).toLocaleDateString('default', {
          month: 'long',
          year: 'numeric',
        });

  return (
    <BasicInfoBox data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM}>
      <Stack direction="row" justifyContent="space-between">
        <StackRow
          direction="row"
          data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_TITLE}
        >
          <Subtitle>{position.title}</Subtitle>,&nbsp;
          <Subtitle> {position.organization.name}</Subtitle>
        </StackRow>
        <Chip
          data-testid={ATSPrincipalAppCandidateModalTabsTestIds.PREVIOUS_POSITION_CHIP}
          sx={{ backgroundColor: theme.palette.white.darker }}
          label={position.title}
        />
      </Stack>
      <StackRow
        direction="row"
        sx={{ mt: 0 }}
        data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_DATES}
      >
        <DateText>
          {new Date(position.start_date).toLocaleDateString('default', {
            month: 'long',
            year: 'numeric',
          })}
        </DateText>
        &nbsp;-&nbsp;
        <DateText>{endDate}</DateText>
      </StackRow>
      <StackRow data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_DESCRIPTION}>
        <Typography component="span">
          <Subtitle sx={{ display: 'inline' }}>Job description:&nbsp;</Subtitle>
          <GreyText sx={{ display: 'inline' }}>{position.description}</GreyText>
        </Typography>
      </StackRow>
      {position.reference_name && (
        <StackRow data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_SUPERVISOR}>
          <Typography component="span">
            <Subtitle sx={{ display: 'inline' }}>Supervisor: </Subtitle>&nbsp;
            <GreyText sx={{ display: 'inline' }}>
              {position.reference_name}
              {(position.reference_phone || position.reference_email) && ' ·'}
            </GreyText>
            &nbsp;
            {position.reference_phone && (
              <GreyText sx={{ display: 'inline' }}>
                {position.reference_phone}
                {position.reference_email && ' ·'}
              </GreyText>
            )}
            {position.reference_phone && position.reference_email && <>&nbsp;</>}
            {position.reference_email && (
              <GreyText sx={{ display: 'inline' }}>{position.reference_email}</GreyText>
            )}
          </Typography>
        </StackRow>
      )}
      {position.reason_for_leaving && (
        <StackRow
          data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_REASON_FOR_LEAVING}
        >
          <Typography component="span">
            <Subtitle sx={{ display: 'inline' }}>Reason for leaving:</Subtitle>&nbsp;
            <GreyText sx={{ display: 'inline' }}>{position.reason_for_leaving}</GreyText>
          </Typography>
        </StackRow>
      )}
      {position.do_not_contact && (
        <ItalicsText
          color="error"
          data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_DO_NOT_CONTACT}
        >
          Please do not contact
        </ItalicsText>
      )}
      {position.presently_employed && (
        <ItalicsText
          data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_ITEM_CURRENTLY_EMPLOYED}
        >
          Currently employed here
        </ItalicsText>
      )}
    </BasicInfoBox>
  );
};

export const ExperienceTab: React.FC<ExperienceTabProps> = ({ previousPositions }) => {
  const titleText =
    previousPositions.length === 1
      ? 'Work History - 1 Entry'
      : `Work History - ${previousPositions.length} Entries`;

  return (
    <Box data-testid={ATSPrincipalAppCandidateModalTabsTestIds.EXPERIENCE_TAB}>
      <SubHeader variant="h3" sx={{ mb: 2 }}>
        {titleText}
      </SubHeader>
      {previousPositions.length === 0 && <NoneFoundText>No experience found</NoneFoundText>}
      {previousPositions.map((position, index) => (
        <ExperienceItem key={index} position={position} />
      ))}
    </Box>
  );
};
